import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { useToast } from '../hooks/useToast';
import ContactModal from '../components/contacts/ContactModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { ToastContainer } from '../components/common/Toast';
import { getInitials } from '../utils/formatters';

const Contacts = () => {
  const navigate = useNavigate();
  const { contacts, loading, addContact, updateContact, deleteContact } = useContacts();
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const filteredContacts = useMemo(() => {
    let result = contacts;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.company?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [contacts, searchQuery]);

  const handleAddContact = () => {
    setEditingContact(null);
    setIsModalOpen(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleSubmitContact = async (formData) => {
    setModalLoading(true);
    try {
      if (editingContact) {
        await updateContact(editingContact.id, formData);
        toast.success('Contact updated successfully');
      } else {
        await addContact(formData);
        toast.success('Contact created successfully');
      }
      setIsModalOpen(false);
      setEditingContact(null);
    } catch (error) {
      toast.error(error.message || 'Failed to save contact');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteContact = async (contactId, contactName) => {
    if (window.confirm(`Are you sure you want to delete ${contactName}?`)) {
      try {
        await deleteContact(contactId);
        toast.success('Contact deleted successfully');
      } catch (error) {
        toast.error(error.message || 'Failed to delete contact');
      }
    }
  };

  const exportToCSV = () => {
    if (contacts.length === 0) {
      toast.warning('No contacts to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Company', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...contacts.map(c => [
        `"${c.name || ''}"`,
        `"${c.email || ''}"`,
        `"${c.phone || ''}"`,
        `"${c.company || ''}"`,
        `"${(c.notes || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Contacts exported successfully');
  };

  return (
    <div className="flex-1 flex flex-col relative bg-page">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSubmit={handleSubmitContact}
        loading={modalLoading}
      />

      {/* Header */}
      <div className="sticky -top-4 md:-top-8 px-6 py-5 border-b border-neutral-200 bg-white backdrop-blur-sm z-[200]">
        <div className="max-w-[1200px] mx-auto w-full flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="font-display text-3xl text-neutral-900 tracking-tight">
                Contact Management
              </h1>
              <p className="text-neutral-500 text-sm">
                View and manage your contacts ({contacts.length} total)
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToCSV}
                disabled={contacts.length === 0}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white border border-neutral-200 text-neutral-700 text-sm font-medium hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[20px]">file_download</span>
                <span className="truncate">Export CSV</span>
              </button>
              <button
                onClick={handleAddContact}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-semibold hover:bg-primary-500 transition-all shadow-md shadow-primary/25 hover:-translate-y-0.5"
              >
                <span className="material-symbols-outlined text-[20px]">add</span>
                <span className="hidden sm:inline">New Contact</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex w-full md:max-w-[320px] items-center rounded-lg bg-white border border-neutral-200 h-10 focus-within:ring-2 focus-within:ring-primary-200 focus-within:border-primary transition-all">
            <div className="pl-3 pr-2 text-neutral-400 flex items-center justify-center">
              <span className="material-symbols-outlined text-[20px]">search</span>
            </div>
            <input
              className="w-full bg-transparent border-none text-sm text-neutral-900 placeholder-neutral-400 focus:ring-0 px-0 h-full outline-none"
              placeholder="Search by name, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
        <div className="max-w-[1200px] mx-auto w-full">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" message="Loading contacts..." />
            </div>
          ) : filteredContacts.length === 0 ? (
            <EmptyState
              icon={searchQuery ? 'search_off' : 'contacts'}
              title={searchQuery ? 'No contacts found' : 'No contacts yet'}
              description={searchQuery ? 'Try adjusting your search' : 'Get started by adding your first contact'}
              actionLabel={!searchQuery ? 'Add Contact' : null}
              onAction={!searchQuery ? handleAddContact : null}
            />
          ) : (
            <>
              <div className="flex overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <table className="flex-1 w-full min-w-[800px]">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="px-6 py-4 text-left w-[250px] text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left w-[200px] text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        Company
                      </th>
                      <th className="px-6 py-4 text-left w-[180px] text-xs font-semibold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">
                        Phone
                      </th>
                      <th className="px-6 py-4 text-right w-[80px] text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredContacts.map((contact) => (
                        <tr
                          key={contact.id}
                          className="hover:bg-neutral-50 transition-colors group cursor-pointer"
                          onClick={() => navigate(`/contact-details?id=${contact.id}`)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="size-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-xs">
                                {getInitials(contact.name)}
                              </div>
                              <div className="flex flex-col">
                                <p className="text-neutral-900 text-sm font-semibold">{contact.name}</p>
                                <p className="text-neutral-500 text-xs">{contact.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-neutral-600 text-sm">{contact.company || '-'}</p>
                          </td>
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <p className="text-neutral-900 text-sm">{contact.phone || '-'}</p>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditContact(contact);
                                }}
                                className="text-neutral-400 hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-neutral-100"
                                title="Edit contact"
                              >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteContact(contact.id, contact.name);
                                }}
                                className="text-neutral-400 hover:text-error transition-colors p-1.5 rounded-lg hover:bg-error-light"
                                title="Delete contact"
                              >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-2 py-4">
                <p className="text-xs text-neutral-500">
                  Showing {filteredContacts.length} of {contacts.length} contacts
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contacts;
